import React from 'react';
import Button from '@/components/ui/Button';

interface AppErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class AppErrorBoundary extends React.Component<
  React.PropsWithChildren,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Could add logging service hook here
    // eslint-disable-next-line no-console
    console.error('AppErrorBoundary caught error', error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-50 p-6 text-center">
          <div className="max-w-md space-y-6">
            <div>
              <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
                Something went wrong
              </h1>
              <p className="text-neutral-600 text-sm">
                An unexpected error occurred. You can try reloading the page.
              </p>
            </div>
            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <pre className="text-left bg-neutral-900 text-neutral-100 p-4 rounded text-xs overflow-auto max-h-60">
                {this.state.error.message}\n{this.state.error.stack}
              </pre>
            )}
            <div className="flex items-center justify-center space-x-3">
              <Button onClick={this.handleReload}>Reload</Button>
              <Button
                variant="secondary"
                onClick={() => (window.location.href = '/cases')}
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
